import { Component, OnInit } from '@angular/core';
import {
  AbstractControl,
  AsyncValidatorFn,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { of, timer } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import { ResourceCreatorService } from '../../../../services/resource-creator.service';
import { ResourceMasterDataService } from '../../../../services/resource-master-data.service';
import { SoftwareFilterService } from '../../../../services/software-filter.service';
import { Pair } from '../../../../models/pair';
import { Log, IShortLogger } from '../../../../services/logger.service';

interface TypedPair extends Pair { type?: string; }

@Component({
  selector: 'app-vm-create-wizard',
  templateUrl: './vm-create-wizard.component.html',
  styleUrl: './vm-create-wizard.component.css',
})
export class VmCreateWizardComponent implements OnInit {
  private readonly logger: IShortLogger<VmCreateWizardComponent> = Log.create(VmCreateWizardComponent);

  // ── Forms ──────────────────────────────────────────────────────────────────
  templateDetailsForm!: FormGroup;
  cloudProviderForm!: FormGroup;
  softwareForm!: FormGroup;

  isSubmitting = false;

  // ── Cloud Provider ─────────────────────────────────────────────────────────
  regionOptions: Pair[] = [];
  sizeOptions: Pair[] = [];
  storageOptions: Pair[] = [];

  regionSearch = '';
  sizeSearch = '';
  storageSearch = '';
  filteredRegions: Pair[] = [];
  filteredSizes: Pair[] = [];
  filteredStorages: Pair[] = [];

  // ── Software ───────────────────────────────────────────────────────────────
  osOptions: Pair[] = [];
  selectedOs = 'windows';
  osVersionOptions: Pair[] = [];
  osVersionSearch = '';
  filteredOsVersions: Pair[] = [];

  osCompatibleSdkOptions: TypedPair[] = [];
  osCompatibleDbOptions: TypedPair[] = [];
  osCompatibleEditorOptions: Pair[] = [];
  osCompatibleMiscOptions: Pair[] = [];

  sdkOptions: TypedPair[] = [];
  databaseOptions: TypedPair[] = [];
  editorOptions: Pair[] = [];
  miscOptions: Pair[] = [];

  sdkSearch = '';
  dbSearch = '';
  editorSearch = '';
  miscSearch = '';

  filteredSdkOptions: TypedPair[] = [];
  filteredDbOptions: TypedPair[] = [];
  filteredEditorOptions: Pair[] = [];
  filteredMiscOptions: Pair[] = [];

  selectedSdks: string[] = [];
  selectedDatabases: string[] = [];
  selectedEditors: string[] = [];
  selectedMisc: string[] = [];

  constructor(
    private readonly fb: FormBuilder,
    private readonly resourceCreatorService: ResourceCreatorService,
    private readonly masterData: ResourceMasterDataService,
    private readonly softwareFilterService: SoftwareFilterService,
    readonly router: Router,
    private readonly snackBar: MatSnackBar,
  ) { }

  ngOnInit(): void {
    this.buildForms();
    this.loadCloudProviderData();
    this.loadOsOptions();
    this.loadOsVersions();
    this.loadSoftwareOptions();
  }

  // ── Form builders ──────────────────────────────────────────────────────────

  private buildForms(): void {
    this.templateDetailsForm = this.fb.group({
      templateName: ['', [Validators.required, Validators.pattern(/^[a-zA-Z0-9_.\-]+$/)], [this.uniqueNameValidator()]],
      templateDescription: [''],
    });

    this.cloudProviderForm = this.fb.group({
      provider: ['azure', Validators.required],
      region: ['', Validators.required],
      size: ['', Validators.required],
      storage: ['', Validators.required],
    });

    this.softwareForm = this.fb.group({
      os: ['windows', Validators.required],
      osVersion: ['', Validators.required],
      hyperV: [false],
    });
  }

  get isLinux(): boolean { return this.selectedOs === 'linux'; }

  /** Debounced async validator: name must be globally unique. */
  private uniqueNameValidator(): AsyncValidatorFn {
    return (control: AbstractControl) => {
      const name = (control.value ?? '').trim();
      if (!name) return of(null);
      return timer(500).pipe(
        switchMap(() => this.resourceCreatorService.getAll()),
        map((resources: any[]) => {
          const taken = resources.some(r => {
            const n = (r.data?.['TemplateDetailsData']?.templateName ?? r.metaData?.name ?? '').trim();
            return n.toLowerCase() === name.toLowerCase();
          });
          return taken ? { nameTaken: true } : null;
        }),
      );
    };
  }

  // ── Cloud Provider data loading ────────────────────────────────────────────

  private loadCloudProviderData(): void {
    const provider = 'azure';

    this.regionOptions = this.masterData.get<Pair[]>(`cloud.${provider}.regions`) ?? [];
    this.sizeOptions = this.masterData.get<Pair[]>(`cloud.${provider}.sizes`) ?? [];
    this.storageOptions = this.masterData.get<Pair[]>(`cloud.${provider}.storage`) ?? [];

    this.filteredRegions = this.regionOptions.slice(0, 30);
    this.filteredSizes = this.sizeOptions.slice(0, 30);
    this.filteredStorages = this.storageOptions.slice(0, 30);
  }

  filterRegions(q: string | any): void {
    if (typeof q !== 'string') return;
    const lower = q.toLowerCase();
    this.filteredRegions = lower
      ? this.regionOptions.filter(r => r.name.toLowerCase().includes(lower) || r.id.toLowerCase().includes(lower))
      : this.regionOptions.slice(0, 30);
  }

  filterSizes(q: string | any): void {
    if (typeof q !== 'string') return;
    const lower = q.toLowerCase();
    this.filteredSizes = lower
      ? this.sizeOptions.filter(s => s.name.toLowerCase().includes(lower) || s.id.toLowerCase().includes(lower))
      : this.sizeOptions.slice(0, 30);
  }

  filterStorages(q: string | any): void {
    if (typeof q !== 'string') return;
    const lower = q.toLowerCase();
    this.filteredStorages = lower
      ? this.storageOptions.filter(s => s.name.toLowerCase().includes(lower) || s.id.toLowerCase().includes(lower))
      : this.storageOptions.slice(0, 30);
  }

  onRegionSelected(pair: Pair): void {
    this.regionSearch = pair.name;
    this.cloudProviderForm.patchValue({ region: pair.id });
    this.cloudProviderForm.get('region')!.markAsTouched();
  }

  onSizeSelected(pair: Pair): void {
    this.sizeSearch = pair.name;
    this.cloudProviderForm.patchValue({ size: pair.id });
    this.cloudProviderForm.get('size')!.markAsTouched();
  }

  onStorageSelected(pair: Pair): void {
    this.storageSearch = pair.name;
    this.cloudProviderForm.patchValue({ storage: pair.id });
    this.cloudProviderForm.get('storage')!.markAsTouched();
  }

  /** Clear hidden control + search string if user types free text and blurs without selecting. */
  validateCloudFieldOnBlur(field: 'region' | 'size' | 'storage'): void {
    const searchMap = { region: this.regionSearch, size: this.sizeSearch, storage: this.storageSearch };
    const optionMap = { region: this.regionOptions, size: this.sizeOptions, storage: this.storageOptions };
    const typed = (searchMap[field] ?? '').trim();
    const valid = optionMap[field].some(o => o.name === typed || o.id === typed);
    if (!valid) {
      if (field === 'region') this.regionSearch = '';
      if (field === 'size') this.sizeSearch = '';
      if (field === 'storage') this.storageSearch = '';
      this.cloudProviderForm.patchValue({ [field]: '' });
    }
    this.cloudProviderForm.get(field)!.markAsTouched();
  }

  // ── Software data loading ──────────────────────────────────────────────────

  private loadOsOptions(): void {
    const osData = this.masterData.get<Record<string, { name: string; isEnabled?: boolean; versions: Pair[] }>>('software.os');
    if (!osData) return;
    this.osOptions = Object.entries(osData)
      .filter(([, group]) => group.isEnabled !== false)
      .map(([id, group]) => ({ id, name: group.name }));
  }

  onOsSelect(osId: string): void {
    this.selectedOs = osId;
    this.softwareForm.patchValue({ os: osId, osVersion: '' });
    this.osVersionSearch = '';
    this.selectedSdks = [];
    this.selectedDatabases = [];
    this.selectedEditors = [];
    this.selectedMisc = [];
    this.loadOsVersions(osId);
    if (this.isLinux) {
      this.softwareForm.patchValue({ hyperV: false });
    }
    // Re-filter software options when OS selection changes
    this.reloadSoftwareOptions(osId);
  }

  private loadOsVersions(osId = 'windows'): void {
    const osData = this.masterData.get<{ name: string; versions: Array<Pair & { isEnabled?: boolean }> }>(`software.os.${osId}`);
    this.osVersionOptions = (osData?.versions ?? []).filter(v => v.isEnabled !== false);
    this.filteredOsVersions = this.osVersionOptions.slice();
    if (this.osVersionOptions.length > 0) {
      const currentId = this.softwareForm.get('osVersion')?.value;
      const match = this.osVersionOptions.find(v => v.id === currentId);
      if (!match) {
        // Only set default if current value is not valid
        const defaultId = this.osVersionOptions[0].id;
        this.softwareForm.patchValue({ osVersion: defaultId });
        this.osVersionSearch = this.osVersionOptions[0].name;
      } else {
        // Keep the current selection and update the search box
        this.osVersionSearch = match.name;
      }
    }
  }

  private loadSoftwareOptions(): void {
    const sdks = this.masterData.get<TypedPair[]>('software.sdks') ?? [];
    this.sdkOptions = sdks;

    const dbs = this.masterData.get<TypedPair[]>('software.databases') ?? [];
    this.databaseOptions = dbs;

    const editors = this.masterData.get<Pair[]>('software.editors') ?? [];
    this.editorOptions = editors;

    const misc = this.masterData.get<Pair[]>('software.misc') ?? [];
    this.miscOptions = misc;

    // Apply filtering for the initially selected OS
    this.reloadSoftwareOptions(this.selectedOs);
  }

  /**
   * Re-filters software options based on selected OS type and version.
   * This is called when OS selection changes or initially loaded.
   * 
   * Docker will be filtered out for Windows, visible for Linux.
   * Postman and other multi-platform software remain visible for both.
   * 
   * @param osType The selected OS type ('windows' or 'linux')
   */
  private reloadSoftwareOptions(osType: string): void {
    // Get the selected OS version from form (dynamically from user selection)
    const osVersion = this.softwareForm.get('osVersion')?.value;
    
    //  ADDED DEBUG LOG 
    this.logger.d(`Reloading software options for OS: ${osType}, Version: ${osVersion}`);

    // Only filter if both osType and osVersion are available
    if (osType && osVersion) {
      // CHANGED target arrays to osCompatible 
      this.osCompatibleSdkOptions = this.softwareFilterService.filterByOs(this.sdkOptions, osType, osVersion);
      this.osCompatibleDbOptions = this.softwareFilterService.filterByOs(this.databaseOptions, osType, osVersion);
      this.osCompatibleEditorOptions = this.softwareFilterService.filterByOs(this.editorOptions, osType, osVersion);
      this.osCompatibleMiscOptions = this.softwareFilterService.filterByOs(this.miscOptions, osType, osVersion);
    } 
    
    // ADDED: Initialize filtered views with the OS compatible lists 
    this.filteredSdkOptions = [...this.osCompatibleSdkOptions];
    this.filteredDbOptions = [...this.osCompatibleDbOptions];
    this.filteredEditorOptions = [...this.osCompatibleEditorOptions];
    this.filteredMiscOptions = [...this.osCompatibleMiscOptions];

    // Clear search filters to show all compatible options
    this.sdkSearch = '';
    this.dbSearch = '';
    this.editorSearch = '';
    this.miscSearch = '';
  }

  filterOsVersions(q: string | any): void {
    if (typeof q !== 'string') return;
    const lower = q.toLowerCase();
    this.filteredOsVersions = lower
      ? this.osVersionOptions.filter(v => v.name.toLowerCase().includes(lower))
      : this.osVersionOptions.slice();
  }

  onOsVersionSelected(v: Pair): void {
    this.osVersionSearch = v.name;
    this.softwareForm.patchValue({ osVersion: v.id });
    this.softwareForm.get('osVersion')!.markAsTouched();
    this.reloadSoftwareOptions(this.selectedOs);
  }

  validateOsVersionOnBlur(): void {
    const typed = (this.osVersionSearch ?? '').trim();
    // Try to find by name or id
    const match = this.osVersionOptions.find(o => o.name === typed || o.id === typed);
    if (match) {
      this.softwareForm.patchValue({ osVersion: match.id });
      this.osVersionSearch = match.name;
      this.reloadSoftwareOptions(this.selectedOs);
    } else {
      this.osVersionSearch = '';
      this.softwareForm.patchValue({ osVersion: '' });
    }
    this.softwareForm.get('osVersion')!.markAsTouched();
  }

  // ── Chip autocomplete helpers ──────────────────────────────────────────────

  filterSdks(q: string): void {
    if (typeof q !== 'string') q = '';
    const lower = q.toLowerCase();
    // CHANGED source array from this.sdkOptions to this.osCompatibleSdkOptions 
    this.filteredSdkOptions = this.osCompatibleSdkOptions.filter(
      o => !this.selectedSdks.includes(o.id) && o.name.toLowerCase().includes(lower));
  }

  filterDbs(q: string): void {
    if (typeof q !== 'string') q = '';
    const lower = q.toLowerCase();
    // CHANGED source array from this.databaseOptions to this.osCompatibleDbOptions 
    this.filteredDbOptions = this.osCompatibleDbOptions.filter(
      o => !this.selectedDatabases.includes(o.id) && o.name.toLowerCase().includes(lower));
  }

  filterEditors(q: string): void {
    if (typeof q !== 'string') q = '';
    const lower = q.toLowerCase();
    // CHANGED source array from this.editorOptions to this.osCompatibleEditorOptions 
    this.filteredEditorOptions = this.osCompatibleEditorOptions.filter(
      o => !this.selectedEditors.includes(o.id) && o.name.toLowerCase().includes(lower));
  }

  filterMisc(q: string): void {
    if (typeof q !== 'string') q = '';
    const lower = q.toLowerCase();
    // CHANGED source array from this.miscOptions to this.osCompatibleMiscOptions 
    this.filteredMiscOptions = this.osCompatibleMiscOptions.filter(
      o => !this.selectedMisc.includes(o.id) && o.name.toLowerCase().includes(lower));
  }

  addChip(field: 'sdks' | 'databases' | 'editors' | 'misc', pair: Pair, inputEl: HTMLInputElement): void {
    const target = this[field === 'sdks' ? 'selectedSdks' : field === 'databases' ? 'selectedDatabases' : field === 'editors' ? 'selectedEditors' : 'selectedMisc'] as string[];
    if (!target.includes(pair.id)) target.push(pair.id);
    inputEl.value = '';
    if (field === 'sdks') { this.sdkSearch = ''; this.filterSdks(''); }
    else if (field === 'databases') { this.dbSearch = ''; this.filterDbs(''); }
    else if (field === 'editors') { this.editorSearch = ''; this.filterEditors(''); }
    else { this.miscSearch = ''; this.filterMisc(''); }
  }

  removeChip(field: 'sdks' | 'databases' | 'editors' | 'misc', id: string): void {
    const targetMap = {
      sdks: this.selectedSdks,
      databases: this.selectedDatabases,
      editors: this.selectedEditors,
      misc: this.selectedMisc
    };

    const target = targetMap[field];
    const idx = target.indexOf(id);

    if (idx >= 0) {
      target.splice(idx, 1);
      
      // Function map 
      const filterMap = {
        sdks: () => this.filterSdks(this.sdkSearch),
        databases: () => this.filterDbs(this.dbSearch),
        editors: () => this.filterEditors(this.editorSearch),
        misc: () => this.filterMisc(this.miscSearch)
      };

      // Execute the mapped function to refresh the dropdown
      filterMap[field]();
    }
  }

  private getChipLabel(options: Pair[], id: string): string {
    return options.find(o => o.id === id)?.name ?? id;
  }

  getSdkLabel(id: string): string { return this.getChipLabel(this.sdkOptions, id); }
  getDbLabel(id: string): string { return this.getChipLabel(this.databaseOptions, id); }
  getEditorLabel(id: string): string { return this.getChipLabel(this.editorOptions, id); }
  getMiscLabel(id: string): string { return this.getChipLabel(this.miscOptions, id); }

  /** displayWith function for single-value autocompletes — prevents [object Object] */
  displayPairFn = (pair: Pair | string | null | undefined): string => {
    if (!pair) return '';
    if (typeof pair === 'string') return pair;
    return (pair as Pair).name;
  };

  // ── Review helpers ─────────────────────────────────────────────────────────

  get reviewTemplateName(): string { return (this.templateDetailsForm.value.templateName ?? '').trim(); }
  get reviewTemplateDesc(): string { return (this.templateDetailsForm.value.templateDescription ?? '').trim(); }
  get reviewRegionLabel(): string { return this.regionOptions.find(r => r.id === this.cloudProviderForm.value.region)?.name ?? this.cloudProviderForm.value.region ?? ''; }
  get reviewSizeLabel(): string { return this.sizeOptions.find(s => s.id === this.cloudProviderForm.value.size)?.name ?? this.cloudProviderForm.value.size ?? ''; }
  get reviewStorageLabel(): string { return this.storageOptions.find(s => s.id === this.cloudProviderForm.value.storage)?.name ?? this.cloudProviderForm.value.storage ?? ''; }
  get reviewOsLabel(): string { return this.osOptions.find(o => o.id === this.selectedOs)?.name ?? this.selectedOs; }
  get reviewOsVersionLabel(): string { return this.osVersionOptions.find(v => v.id === this.softwareForm.value.osVersion)?.name ?? this.softwareForm.value.osVersion ?? ''; }

  // ── Submit ─────────────────────────────────────────────────────────────────

  onSubmit(): void {
    if (this.templateDetailsForm.invalid || this.cloudProviderForm.invalid || this.softwareForm.invalid || this.isSubmitting) return;

    this.isSubmitting = true;

    const finalData: Record<string, any> = {
      TemplateDetailsData: {
        name: 'Template Details',
        templateName: this.reviewTemplateName,
        templateDescription: this.reviewTemplateDesc,
      },
      CloudProviderData: {
        name: 'Cloud Provider',
        provider: this.cloudProviderForm.value.provider,
        region: this.cloudProviderForm.value.region,
        size: this.cloudProviderForm.value.size,
        storage: this.cloudProviderForm.value.storage,
      },
      SoftwareData: {
        name: 'Software Configuration',
        os: this.softwareForm.value.os,
        osVersion: this.softwareForm.value.osVersion,
        hyperV: this.softwareForm.value.hyperV,
        sdks: [...this.selectedSdks],
        databases: [...this.selectedDatabases],
        editors: [...this.selectedEditors],
        misc: [...this.selectedMisc],
      },
    };

    const config: any = {
      name: finalData['TemplateDetailsData'].templateName,
      type: 'vm',
      description: finalData['TemplateDetailsData'].templateDescription,
      plugins: [],
    };

    this.resourceCreatorService.create(finalData as any, config).subscribe({
      next: () => {
        this.isSubmitting = false;
        this.logger.i('VM template created successfully');
        this.router.navigate(['/templates'], { queryParams: { tab: 'vm' } });
      },
      error: (err) => {
        this.isSubmitting = false;
        this.logger.e('Failed to create VM template', err);
        this.snackBar.open('Failed to create VM template. Please try again.', 'Dismiss', { duration: 5000 });
      },
    });
  }
}
